import React, { Suspense, lazy, Component } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AppLayout } from '../layouts/AppLayout';
import { PageTransitionWrapper } from '../../shared/ui/PageTransitionWrapper';
import { ProtectedRoute } from './ProtectedRoute';
import { LoadingScreen } from '../../shared/ui/LoadingScreen';
import { Login } from '../../features/auth/pages/Login';
// Lazy-loaded feature pages. Named exports are mapped to default for lazy.
const lazyNamed = <T extends Record<string, unknown>,>(loader: () => Promise<T>, name: keyof T) => lazy(() => loader().then(m => ({
  default: m[name] as ComponentType<unknown>
})));
// analytics
const Overview = lazyNamed(() => import('../../features/analytics'), 'Overview');
const CustomMetrics = lazyNamed(() => import('../../features/analytics'), 'CustomMetrics');
const Nadi = lazyNamed(() => import('../../features/analytics'), 'Nadi');
const Reports = lazyNamed(() => import('../../features/analytics'), 'Reports');
const CardMonitoringPrograms = lazyNamed(() => import('../../features/analytics'), 'CardMonitoringPrograms');
const VampDetail = lazyNamed(() => import('../../features/analytics'), 'VampDetail');
const PaymentAnalytics = lazyNamed(() => import('../../features/analytics'), 'PaymentAnalytics');
// notifications
const Notifications = lazyNamed(() => import('../../features/notifications'), 'Notifications');
// payments
const PaymentRequests = lazyNamed(() => import('../../features/payments'), 'PaymentRequests');
const BulkPayments = lazyNamed(() => import('../../features/payments'), 'BulkPayments');
const BulkPaymentDetail = lazyNamed(() => import('../../features/payments'), 'BulkPaymentDetail');
const VirtualTerminal = lazyNamed(() => import('../../features/payments'), 'VirtualTerminal');
const QRCodes = lazyNamed(() => import('../../features/payments'), 'QRCodes');
// settings
const Settings = lazyNamed(() => import('../../features/settings'), 'Settings');
const UpdatePayoutAccount = lazyNamed(() => import('../../features/settings'), 'UpdatePayoutAccount');
// balance
const PayoutSettings = lazyNamed(() => import('../../features/balance'), 'PayoutSettings');
const BalanceSummary = lazyNamed(() => import('../../features/balance'), 'BalanceSummary');
const BalanceActivity = lazyNamed(() => import('../../features/balance'), 'BalanceActivity');
const BalanceActivityDetail = lazyNamed(() => import('../../features/balance'), 'BalanceActivityDetail');
const Payouts = lazyNamed(() => import('../../features/balance'), 'Payouts');
const PayoutDetail = lazyNamed(() => import('../../features/balance'), 'PayoutDetail');
// terminals
const PhysicalTerminals = lazyNamed(() => import('../../features/terminals'), 'PhysicalTerminals');
const SoftPosTerminals = lazyNamed(() => import('../../features/terminals'), 'SoftPosTerminals');
const Connect = lazyNamed(() => import('../../features/terminals'), 'Connect');
// developers
const WebhookEndpoints = lazyNamed(() => import('../../features/developers'), 'WebhookEndpoints');
const WebhookLogs = lazyNamed(() => import('../../features/developers'), 'WebhookLogs');
// tutorials
const Tutorials = lazyNamed(() => import('../../features/tutorials'), 'Tutorials');
const TutorialDetail = lazyNamed(() => import('../../features/tutorials'), 'TutorialDetail');
// shops (orders, shops, campaigns, sales, inventory)
const Orders = lazyNamed(() => import('../../features/shops'), 'Orders');
const OrderDetail = lazyNamed(() => import('../../features/shops'), 'OrderDetail');
const Shops = lazyNamed(() => import('../../features/shops'), 'Shops');
const ShopDetails = lazyNamed(() => import('../../features/shops'), 'ShopDetails');
const Campaigns = lazyNamed(() => import('../../features/shops'), 'Campaigns');
const CampaignDetails = lazyNamed(() => import('../../features/shops'), 'CampaignDetails');
const CampaignForm = lazyNamed(() => import('../../features/shops'), 'CampaignForm');
const Inventory = lazyNamed(() => import('../../features/shops'), 'Inventory');
const Sales = lazyNamed(() => import('../../features/shops'), 'Sales');
const SaleDetail = lazyNamed(() => import('../../features/shops'), 'SaleDetail');
const ShopSettings = lazyNamed(() => import('../../features/shops'), 'ShopSettings');
const ProductDetail = lazyNamed(() => import('../../features/shops'), 'ProductDetail');
const ProductEdit = lazyNamed(() => import('../../features/shops'), 'ProductEdit');
// rooms
const PropertyInformation = lazyNamed(() => import('../../features/rooms'), 'PropertyInformation');
const Rooms = lazyNamed(() => import('../../features/rooms'), 'Rooms');
const AddRoom = lazyNamed(() => import('../../features/rooms'), 'AddRoom');
const EditRoom = lazyNamed(() => import('../../features/rooms'), 'EditRoom');
const RoomDetail = lazyNamed(() => import('../../features/rooms'), 'RoomDetail');
const Rates = lazyNamed(() => import('../../features/rooms'), 'Rates');
const Reservations = lazyNamed(() => import('../../features/rooms'), 'Reservations');
const ReservationDetail = lazyNamed(() => import('../../features/rooms'), 'ReservationDetail');
// invoices
const Invoices = lazyNamed(() => import('../../features/invoices'), 'Invoices');
const InvoiceDetail = lazyNamed(() => import('../../features/invoices'), 'InvoiceDetail');
const InvoiceForm = lazyNamed(() => import('../../features/invoices'), 'InvoiceForm');
// customers
const Customers = lazyNamed(() => import('../../features/customers'), 'Customers');
const CustomerDetail = lazyNamed(() => import('../../features/customers'), 'CustomerDetail');
const CustomerForm = lazyNamed(() => import('../../features/customers'), 'CustomerForm');
// suppliers
const Suppliers = lazyNamed(() => import('../../features/suppliers'), 'Suppliers');
const SupplierDetail = lazyNamed(() => import('../../features/suppliers'), 'SupplierDetail');
const SupplierForm = lazyNamed(() => import('../../features/suppliers'), 'SupplierForm');
const SupplierSelfService = lazyNamed(() => import('../../features/suppliers'), 'SupplierSelfService');
const BulkSupplierUpload = lazyNamed(() => import('../../features/suppliers'), 'BulkSupplierUpload');
// bills
const Bills = lazyNamed(() => import('../../features/bills'), 'Bills');
const BillDetail = lazyNamed(() => import('../../features/bills'), 'BillDetail');
const BillForm = lazyNamed(() => import('../../features/bills'), 'BillForm');
const BillApprovalWizard = lazyNamed(() => import('../../features/bills'), 'BillApprovalWizard');
const BillFinancingWizard = lazyNamed(() => import('../../features/bills'), 'BillFinancingWizard');
const BulkBillUpload = lazyNamed(() => import('../../features/bills'), 'BulkBillUpload');
const Financing = lazyNamed(() => import('../../features/bills'), 'Financing');
const FacilityDetail = lazyNamed(() => import('../../features/bills'), 'FacilityDetail');
const FinancingApplyWizard = lazyNamed(() => import('../../features/bills'), 'FinancingApplyWizard');
// disputes
const Disputes = lazyNamed(() => import('../../features/disputes'), 'Disputes');
const DisputeDetail = lazyNamed(() => import('../../features/disputes'), 'DisputeDetail');
const ChallengeDispute = lazyNamed(() => import('../../features/disputes'), 'ChallengeDispute');
// refunds
const Refunds = lazyNamed(() => import('../../features/refunds'), 'Refunds');
const RefundDetail = lazyNamed(() => import('../../features/refunds'), 'RefundDetail');
// system
const ActivityLogs = lazyNamed(() => import('../../features/system'), 'ActivityLogs');
const Requests = lazyNamed(() => import('../../features/system'), 'Requests');
const RequestDetail = lazyNamed(() => import('../../features/system'), 'RequestDetail');
export function AppRouter() {
  return <BrowserRouter data-id="element-235" data-mp="3hcmfktthse9h">
      <Suspense fallback={<LoadingScreen data-id="element-237" data-mp="3hdnyfkgr251r" />} data-id="element-236" data-mp="3hd4lki5yi6ul">
        <Routes data-id="element-238" data-mp="3ldeplpa6un6t">
          <Route path="/" element={<Login data-id="element-240" data-mp="3le7xwuospkr2" />} data-id="element-239" data-mp="3lcwvozb00l22" />
          <Route path="/supplier-onboarding/:id" element={<SupplierSelfService data-id="element-242" data-mp="3qbgfou617mrs" />} data-id="element-241" data-mp="3ldod967rgi9c" />

          <Route path="/overview" element={<ProtectedRoute data-id="element-244" data-mp="3v4ss85xfs346">
                <AppLayout data-id="element-245" data-mp="3wx4158fylp0">
                  <PageTransitionWrapper data-id="element-246" data-mp="3xq27htc42vj">
                    <Overview data-id="element-247" data-mp="3wne1hnecejb" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-243" data-mp="3qaojbh4m1l0g" />
          <Route path="/notifications" element={<ProtectedRoute data-id="element-249" data-mp="hwpowjxae7p2">
                <AppLayout data-id="element-250" data-mp="hwy1eaphzazq">
                  <PageTransitionWrapper data-id="element-251" data-mp="hw6avearc3gb">
                    <Notifications data-id="element-252" data-mp="2ep80hd3nv8mr" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-248" data-mp="cx3ai75q2leo" />
          <Route path="/payment-requests" element={<ProtectedRoute data-id="element-254" data-mp="2c5bq12zaw7gn">
                <AppLayout data-id="element-255" data-mp="2c6mul50klj8t">
                  <PageTransitionWrapper data-id="element-256" data-mp="27e3ea0ai0a0b">
                    <PaymentRequests data-id="element-257" data-mp="27d1un7ff2f6z" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-253" data-mp="2job895b0zsyf" />
          <Route path="/qr-codes" element={<ProtectedRoute data-id="element-259" data-mp="1xdt0oekjx0km">
                <AppLayout data-id="element-260" data-mp="2v3xpyej2zsiz">
                  <PageTransitionWrapper data-id="element-261" data-mp="2v236l4ngk8h0">
                    <QRCodes data-id="element-262" data-mp="2v11q2knoatrw" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-258" data-mp="1xfxblggvazue" />
          <Route path="/bulk-payments" element={<ProtectedRoute data-id="element-264" data-mp="2h4698616jy1a">
                <AppLayout data-id="element-265" data-mp="2h33grto7lnj9">
                  <PageTransitionWrapper data-id="element-266" data-mp="2h5zebgw5aa76">
                    <BulkPayments data-id="element-267" data-mp="19qbfx2zpf2r5" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-263" data-mp="2l3x4on1nj2c5" />
          <Route path="/bulk-payment-detail/:id" element={<ProtectedRoute data-id="element-269" data-mp="2j2ag6cp2l4yz">
                <AppLayout data-id="element-270" data-mp="2f31m16q4b52w">
                  <PageTransitionWrapper data-id="element-271" data-mp="2f3t8ns1qlsh1">
                    <BulkPaymentDetail data-id="element-272" data-mp="2f0oy8cxoi0hx" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-268" data-mp="15quauao6w7mi" />
          <Route path="/settings" element={<ProtectedRoute data-id="element-274" data-mp="32rum9fdrvz3b">
                <AppLayout data-id="element-275" data-mp="32r1b827s6nfd">
                  <PageTransitionWrapper data-id="element-276" data-mp="32ugrv9h3s4iw">
                    <Settings data-id="element-277" data-mp="2xtsvs9e0oxht" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-273" data-mp="2o2odp0t1owir" />
          <Route path="/settings/payouts" element={<ProtectedRoute data-id="element-279" data-mp="36uh88znripsl">
                <AppLayout data-id="element-280" data-mp="36s4u6xl4kzkt">
                  <PageTransitionWrapper data-id="element-281" data-mp="1hehsgzpn223z">
                    <PayoutSettings data-id="element-282" data-mp="1hf9ogbn5e1us" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-278" data-mp="3bqy5qkguqgic" />
          <Route path="/settings/payout-account/edit" element={<ProtectedRoute data-id="element-284" data-mp="3t2weia7mw2md">
                <AppLayout data-id="element-285" data-mp="3t11t6usga8hr">
                  <PageTransitionWrapper data-id="element-286" data-mp="3t4h6pt6h79h2">
                    <UpdatePayoutAccount data-id="element-287" data-mp="3fbhrlsalvha1" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-283" data-mp="3o308y6jag34p" />
          <Route path="/virtual-terminal" element={<ProtectedRoute data-id="element-289" data-mp="35bh8o7gnf36d">
                <AppLayout data-id="element-290" data-mp="3abvhhrq2qh5g">
                  <PageTransitionWrapper data-id="element-291" data-mp="3ac56pd3aoka3">
                    <VirtualTerminal data-id="element-292" data-mp="3a9sgkcndv3ly" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-288" data-mp="3ja75rn3gx2uq" />
          <Route path="/physical-terminals" element={<ProtectedRoute data-id="element-294" data-mp="uk7na42g32qc">
                <AppLayout data-id="element-295" data-mp="um271f1zcosu">
                  <PageTransitionWrapper data-id="element-296" data-mp="uimou3d0ezn7">
                    <PhysicalTerminals data-id="element-297" data-mp="ymm4ykudnwg1" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-293" data-mp="30eqszl8ra9lh" />
          <Route path="/softpos-terminals" element={<ProtectedRoute data-id="element-299" data-mp="22iv82rc47x2q">
                <AppLayout data-id="element-300" data-mp="1yikyz5t10qjn">
                  <PageTransitionWrapper data-id="element-301" data-mp="1yht8g9eadj08">
                    <SoftPosTerminals data-id="element-302" data-mp="1ykp4fs6kpxm1" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-298" data-mp="27gwqrb5mpudo" />
          <Route path="/connect" element={<ProtectedRoute data-id="element-304" data-mp="2m8fexw8n6v84">
                <AppLayout data-id="element-305" data-mp="2m98f2ed3imha">
                  <PageTransitionWrapper data-id="element-306" data-mp="2m8ytr42zx3hx">
                    <Connect data-id="element-307" data-mp="2hgxbn1j5cijz" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-303" data-mp="2q8pk56om1hlx" />
          <Route path="/webhooks/endpoints" element={<ProtectedRoute data-id="element-309" data-mp="39yjgly29skwh">
                <AppLayout data-id="element-310" data-mp="39zksu5v0vdfs">
                  <PageTransitionWrapper data-id="element-311" data-mp="356hwjpyv1pvw">
                    <WebhookEndpoints data-id="element-312" data-mp="355gm9npo57f8" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-308" data-mp="2ci3yja7rutkc" />
          <Route path="/webhooks/logs" element={<ProtectedRoute data-id="element-314" data-mp="3mgdfavohpiv7">
                <AppLayout data-id="element-315" data-mp="3mg3y9xlfam1n">
                  <PageTransitionWrapper data-id="element-316" data-mp="3mesn3cpnec0i">
                    <WebhookLogs data-id="element-317" data-mp="3qhp8tcahio8o" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-313" data-mp="3hgr38pkee8nz" />
          <Route path="/tutorials" element={<ProtectedRoute data-id="element-319" data-mp="49pbra9393rn">
                <AppLayout data-id="element-320" data-mp="48wer0zxlsmp">
                  <PageTransitionWrapper data-id="element-321" data-mp="91h49qbt1jkf">
                    <Tutorials data-id="element-322" data-mp="92iho1gami5b" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-318" data-mp="3vgilx3lv0d8b" />
          <Route path="/tutorial/:id" element={<ProtectedRoute data-id="element-324" data-mp="i3yoy0bta45b">
                <AppLayout data-id="element-325" data-mp="n0yywmbs7r1p">
                  <PageTransitionWrapper data-id="element-326" data-mp="n2a1iithqsr8">
                    <TutorialDetail data-id="element-327" data-mp="mz5r33pfn0s4" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-323" data-mp="i25k8qkrdtzy" />
          <Route path="/orders" element={<ProtectedRoute data-id="element-329" data-mp="j41klxcqaih7">
                <AppLayout data-id="element-330" data-mp="j39s4vefh0v5">
                  <PageTransitionWrapper data-id="element-331" data-mp="j3jjonf4fg2y">
                    <Orders data-id="element-332" data-mp="e4q2ajwpr4xi" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-328" data-mp="5bv7kk4vgrlz" />
          <Route path="/order-detail/:id" element={<ProtectedRoute data-id="element-334" data-mp="3ikrh9602jaip">
                <AppLayout data-id="element-335" data-mp="3ilj5twv907zh">
                  <PageTransitionWrapper data-id="element-336" data-mp="djyibmeil4t">
                    <OrderDetail data-id="element-337" data-mp="fehvli0y56s" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-333" data-mp="3mlbdm7ctia3s" />
          <Route path="/shops" element={<ProtectedRoute data-id="element-339" data-mp="1bv34gbrd9272">
                <AppLayout data-id="element-340" data-mp="16uf42zh8z906">
                  <PageTransitionWrapper data-id="element-341" data-mp="16t40p0rps39l">
                    <Shops data-id="element-342" data-mp="16w9p0dsir149" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-338" data-mp="1bsqh1j2u9zmm" />
          <Route path="/shop-details" element={<ProtectedRoute data-id="element-344" data-mp="2frdm8idoccqd">
                <AppLayout data-id="element-345" data-mp="2fs5h1qzg66fl">
                  <PageTransitionWrapper data-id="element-346" data-mp="2fsx4geiw4xus">
                    <ShopDetails data-id="element-347" data-mp="2kpf798unum4h" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-343" data-mp="2trobxi38zl9y" />
          <Route path="/shop-settings/:shopId" element={<ProtectedRoute data-id="element-349" data-mp="3cgbhe1pc6co2">
                <AppLayout data-id="element-350" data-mp="2yppxc9o5bwol">
                  <PageTransitionWrapper data-id="element-351" data-mp="2yqhtbllnnwfe">
                    <ShopSettings data-id="element-352" data-mp="2ypg6kjva1lhu" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-348" data-mp="38hm6cfrrt77l" />
          <Route path="/shop-settings" element={<ProtectedRoute data-id="element-354" data-mp="3w4u0q2nnuvuu">
                <AppLayout data-id="element-355" data-mp="rctqwv4z66">
                  <PageTransitionWrapper data-id="element-356" data-mp="10vsujhq62d">
                    <ShopSettings data-id="element-357" data-mp="9vevdtj04xg7" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-353" data-mp="3re3tejvocixi" />
          <Route path="/product-detail/:productId" element={<ProtectedRoute data-id="element-359" data-mp="3w1bpkay5cub3">
                <AppLayout data-id="element-360" data-mp="3w4pplcjc8rfd">
                  <PageTransitionWrapper data-id="element-361" data-mp="3r4blu6ors3n5">
                    <ProductDetail data-id="element-362" data-mp="3r3jyfj5btc7y" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-358" data-mp="4xd6ut2t45xc" />
          <Route path="/product-edit/:productId" element={<ProtectedRoute data-id="element-364" data-mp="3dc5bx2c7msw5">
                <AppLayout data-id="element-365" data-mp="3dd6ppekm1thk">
                  <PageTransitionWrapper data-id="element-366" data-mp="3daasxtki1aup">
                    <ProductEdit data-id="element-367" data-mp="38du2gsptukeg" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-363" data-mp="3ib87zxa7porz" />
          <Route path="/campaigns" element={<ProtectedRoute data-id="element-369" data-mp="2zdx9iewemzdl">
                <AppLayout data-id="element-370" data-mp="2zeqi7lexbyyd">
                  <PageTransitionWrapper data-id="element-371" data-mp="1euv44237cnya">
                    <Campaigns data-id="element-372" data-mp="1eu1wkywf5uf3" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-368" data-mp="33gbryxw5vx4c" />
          <Route path="/campaign-details" element={<ProtectedRoute data-id="element-374" data-mp="1ntgj5n9gtkub">
                <AppLayout data-id="element-375" data-mp="1sumgxzhzfyap">
                  <PageTransitionWrapper data-id="element-376" data-mp="1stujej4trqhs">
                    <CampaignDetails data-id="element-377" data-mp="v6m63bez9nqv" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-373" data-mp="1nvjd2idqny4b" />
          <Route path="/campaign-form" element={<ProtectedRoute data-id="element-379" data-mp="153qsnccqjl87">
                <AppLayout data-id="element-380" data-mp="153h29nns3c1z">
                  <PageTransitionWrapper data-id="element-381" data-mp="19via8xdwygm7">
                    <CampaignForm data-id="element-382" data-mp="19wa689bfagd0" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-378" data-mp="1068f18nrwhtv" />
          <Route path="/property-information" element={<ProtectedRoute data-id="element-384" data-mp="hcus9qu7rhpc">
                <AppLayout data-id="element-385" data-mp="36wrckaqs6zya">
                  <PageTransitionWrapper data-id="element-386" data-mp="36y2gqbo529px">
                    <PropertyInformation data-id="element-387" data-mp="36z40d4j804j9" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-383" data-mp="hf7hmp2awucf" />
          <Route path="/rooms" element={<ProtectedRoute data-id="element-389" data-mp="3ayl8k5q17fs4">
                <AppLayout data-id="element-390" data-mp="3axba3ff9o5ky">
                  <PageTransitionWrapper data-id="element-391" data-mp="3b17fq490ft9t">
                    <Rooms data-id="element-392" data-mp="3pv3lpwhp09tv" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-388" data-mp="3fzioscrhdzqz" />
          <Route path="/add-room" element={<ProtectedRoute data-id="element-394" data-mp="2ijfk03hp10e">
                <AppLayout data-id="element-395" data-mp="2kvtm264mr86">
                  <PageTransitionWrapper data-id="element-396" data-mp="3umbxv0aeq9aq">
                    <AddRoom data-id="element-397" data-mp="3unnbdrtnmvf1" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-393" data-mp="3kv7t0teg44tm" />
          <Route path="/edit-room" element={<ProtectedRoute data-id="element-399" data-mp="7gb6orwb8j3j">
                <AppLayout data-id="element-400" data-mp="r3eaq1syfo37">
                  <PageTransitionWrapper data-id="element-401" data-mp="r58wfic1vk8c">
                    <EditRoom data-id="element-402" data-mp="w30pifoflce4" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-398" data-mp="7inx7tg4w1s7" />
          <Route path="/room-detail" element={<ProtectedRoute data-id="element-404" data-mp="m41sjud1356y">
                <AppLayout data-id="element-405" data-mp="m39w6hblx3fm">
                  <PageTransitionWrapper data-id="element-406" data-mp="m6o0hv3tzmpp">
                    <RoomDetail data-id="element-407" data-mp="8d6hqciyye0q" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-403" data-mp="h4ylk4dhmowc" />
          <Route path="/inventory" element={<ProtectedRoute data-id="element-409" data-mp="3um7ks4mixusm">
                <AppLayout data-id="element-410" data-mp="3eczk6sqlyu8">
                  <PageTransitionWrapper data-id="element-411" data-mp="3dk25wfo4lor">
                    <Inventory data-id="element-412" data-mp="3emqbwllwa0z" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-408" data-mp="d5zloy6m5ibr" />
          <Route path="/rates" element={<ProtectedRoute data-id="element-414" data-mp="3pneccqm3gtzb">
                <AppLayout data-id="element-415" data-mp="2fsauc40zdfgn">
                  <PageTransitionWrapper data-id="element-416" data-mp="2fqzg1a9wspba">
                    <Rates data-id="element-417" data-mp="2krnhkpvrkojr" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-413" data-mp="3pltdwpwnsqw6" />
          <Route path="/reservations" element={<ProtectedRoute data-id="element-419" data-mp="2aspkgyk9vmp5">
                <AppLayout data-id="element-420" data-mp="2atzixov1ewwb">
                  <PageTransitionWrapper data-id="element-421" data-mp="2avambnkkm2mw">
                    <Reservations data-id="element-422" data-mp="2yk3c12alne92" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-418" data-mp="26t78isafilyg" />
          <Route path="/reservation-detail" element={<ProtectedRoute data-id="element-424" data-mp="2ponn8zopbpac">
                <AppLayout data-id="element-425" data-mp="2ts3hjpdatdjv">
                  <PageTransitionWrapper data-id="element-426" data-mp="2tra7ofj1m7xi">
                    <ReservationDetail data-id="element-427" data-mp="2tq8ua4ek19cm" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-423" data-mp="33jzjxcmf3ptw" />
          <Route path="/invoices" element={<ProtectedRoute data-id="element-429" data-mp="1at5k11fsy9sf">
                <AppLayout data-id="element-430" data-mp="1asclgnqzyqld">
                  <PageTransitionWrapper data-id="element-431" data-mp="1arknx7duaisg">
                    <Invoices data-id="element-432" data-mp="15v3zsd6n44fd" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-428" data-mp="3m9fuytvqb0ok" />
          <Route path="/invoice-detail/:id" element={<ProtectedRoute data-id="element-434" data-mp="1fkvynjlrvjpn">
                <AppLayout data-id="element-435" data-mp="1fmqcyf2jgdkr">
                  <PageTransitionWrapper data-id="element-436" data-mp="ru9ui3ew2jkx">
                    <InvoiceDetail data-id="element-437" data-mp="rv1oxawr2b9m" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-433" data-mp="1kljnpzqfxqh8" />
          <Route path="/invoice-form" element={<ProtectedRoute data-id="element-439" data-mp="wtw5s7b5ppr3">
                <AppLayout data-id="element-440" data-mp="wsusrxakyt6q">
                  <PageTransitionWrapper data-id="element-441" data-mp="wsl1859w0dyx">
                    <InvoiceForm data-id="element-442" data-mp="2d7w3dbmj53b6" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-438" data-mp="11t935lznmf7x" />
          <Route path="/customers" element={<ProtectedRoute data-id="element-444" data-mp="2fb7v9xxtsvz0">
                <AppLayout data-id="element-445" data-mp="2fbzsfd72n1re">
                  <PageTransitionWrapper data-id="element-446" data-mp="2kbcazlqwtmo6">
                    <Customers data-id="element-447" data-mp="2ka195rh0yozp" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-443" data-mp="288rteirs71js" />
          <Route path="/customer-detail/:id" element={<ProtectedRoute data-id="element-449" data-mp="2u7flpfztutmq">
                <AppLayout data-id="element-450" data-mp="1wm9wgmrko9xw">
                  <PageTransitionWrapper data-id="element-451" data-mp="1wn2umzcgtr4f">
                    <CustomerDetail data-id="element-452" data-mp="1wjx6bmbnut9r" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-448" data-mp="2u9iajwp8ugpv" />
          <Route path="/customer-form" element={<ProtectedRoute data-id="element-454" data-mp="2bawaml5gqvnj">
                <AppLayout data-id="element-455" data-mp="2bc7dmir33zdl">
                  <PageTransitionWrapper data-id="element-456" data-mp="2bch8ajn2qupm">
                    <CustomerForm data-id="element-457" data-mp="1du7cb1fq2biw" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-453" data-mp="26j5yc9mytxth" />
          <Route path="/sales" element={<ProtectedRoute data-id="element-459" data-mp="3w43a0minsgtu">
                <AppLayout data-id="element-460" data-mp="3s31iysywbb2y">
                  <PageTransitionWrapper data-id="element-461" data-mp="3s2rrf0y7cvv5">
                    <Sales data-id="element-462" data-mp="3s3t1p37e9ebt" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-458" data-mp="1is0k1ywobdla" />
          <Route path="/sale-detail/:id" element={<ProtectedRoute data-id="element-464" data-mp="3d5qzawc3myw5">
                <AppLayout data-id="element-465" data-mp="3d60ncedl2vz7">
                  <PageTransitionWrapper data-id="element-466" data-mp="3d7brifaxy5qu">
                    <SaleDetail data-id="element-467" data-mp="38fakb7smr57o" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-463" data-mp="3i5dcx6vuah5h" />
          <Route path="/balance" element={<ProtectedRoute data-id="element-469" data-mp="2zf5fn5etmkx7">
                <AppLayout data-id="element-470" data-mp="2zg70g1ln2ls4">
                  <PageTransitionWrapper data-id="element-471" data-mp="2uf9jttga1zsq">
                    <BalanceSummary data-id="element-472" data-mp="2ue7x2rpwfov6" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-468" data-mp="34dyouln2rprk" />
          <Route path="/balance-activity" element={<ProtectedRoute data-id="element-474" data-mp="tf4mc2wbigrl">
                <AppLayout data-id="element-475" data-mp="ybuzahctsw8s">
                  <PageTransitionWrapper data-id="element-476" data-mp="yb38rky35opd">
                    <BalanceActivity data-id="element-477" data-mp="ydz4r3qdi3b6" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-473" data-mp="td1m6q1gb795" />
          <Route path="/balance-activity/:id" element={<ProtectedRoute data-id="element-479" data-mp="amafjw8uluyg">
                <AppLayout data-id="element-480" data-mp="al8tyxu7hq2h">
                  <PageTransitionWrapper data-id="element-481" data-mp="fln3ylfdba35">
                    <BalanceActivityDetail data-id="element-482" data-mp="fmoe8nok7sjt" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-478" data-mp="ol0ezij546pz" />
          <Route path="/payouts" element={<ProtectedRoute data-id="element-484" data-mp="5pjromqsxv2h">
                <AppLayout data-id="element-485" data-mp="1v2n94x961s24">
                  <PageTransitionWrapper data-id="element-486" data-mp="1v3gkkbj2l5ql">
                    <Payouts data-id="element-487" data-mp="1v0aw8yi9m7vx" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-483" data-mp="5n7c2g8inwsl" />
          <Route path="/payouts/:id" element={<ProtectedRoute data-id="element-489" data-mp="3fkppjqs95t6c">
                <AppLayout data-id="element-490" data-mp="3flr0zwd6khol">
                  <PageTransitionWrapper data-id="element-491" data-mp="3ficxgkss62fk">
                    <PayoutDetail data-id="element-492" data-mp="3u9u9pdily7h3" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-488" data-mp="3kkkgtvfxwkrx" />
          <Route path="/suppliers" element={<ProtectedRoute data-id="element-494" data-mp="73bc5fo4wmo9">
                <AppLayout data-id="element-495" data-mp="76pdqloz4run">
                  <PageTransitionWrapper data-id="element-496" data-mp="33awxst7o7g5">
                    <Suppliers data-id="element-497" data-mp="32homnelt9vw" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-493" data-mp="3pgixewv10yhs" />
          <Route path="/supplier-detail/:id" element={<ProtectedRoute data-id="element-499" data-mp="2iu5ksglsmydn">
                <AppLayout data-id="element-500" data-mp="2itdp75s750nd">
                  <PageTransitionWrapper data-id="element-501" data-mp="2iup2pxbg1mro">
                    <SupplierDetail data-id="element-502" data-mp="2dv2gwzhjuy79" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-498" data-mp="c3z4c4o3n9k2" />
          <Route path="/supplier-form" element={<ProtectedRoute data-id="element-504" data-mp="ms24t6jm36lv">
                <AppLayout data-id="element-505" data-mp="mralov77b1ch">
                  <PageTransitionWrapper data-id="element-506" data-mp="qsm3wfnqwkan">
                    <SupplierForm data-id="element-507" data-mp="qrui1wjya0xk" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-503" data-mp="htik19gr80v3" />
          <Route path="/supplier-bulk-upload" element={<ProtectedRoute data-id="element-509" data-mp="10oz661xcw6h0">
                <AppLayout data-id="element-510" data-mp="15pd2implhrz7">
                  <PageTransitionWrapper data-id="element-511" data-mp="15oli881g7go8">
                    <BulkSupplierUpload data-id="element-512" data-mp="1aglenqib7rg2" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-508" data-mp="10qtkuyi1b2cn" />
          <Route path="/bill-bulk-upload" element={<ProtectedRoute data-id="element-514" data-mp="1jichmnlgtk68">
                <AppLayout data-id="element-515" data-mp="1jj4es2upnpym">
                  <PageTransitionWrapper data-id="element-516" data-mp="1oge3fga9zxle">
                    <BulkBillUpload data-id="element-517" data-mp="1oh5ympzyntb5" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-513" data-mp="1ehovogc3ftiv" />
          <Route path="/bills" element={<ProtectedRoute data-id="element-519" data-mp="1tnb3347hyrum">
                <AppLayout data-id="element-520" data-mp="1olvgv8tl2fan">
                  <PageTransitionWrapper data-id="element-521" data-mp="1on6l19qxxp2a">
                    <Bills data-id="element-522" data-mp="1oo7yfkvfinn6" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-518" data-mp="1tlhu3i9evvjg" />
          <Route path="/bills/financing" element={<ProtectedRoute data-id="element-524" data-mp="2cbzmflqc0p4a">
                <AppLayout data-id="element-525" data-mp="2cd0wbmvm35kf">
                  <PageTransitionWrapper data-id="element-526" data-mp="2cdst310y39ca">
                    <Financing data-id="element-527" data-mp="27khhkml6u4e1" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-523" data-mp="2hb2hqegifgz2" />
          <Route path="/bills/financing/apply" element={<ProtectedRoute data-id="element-529" data-mp="302v6fwojqg0z">
                <FinancingApplyWizard data-id="element-530" data-mp="30352a0w9vhel" />
              </ProtectedRoute>} data-id="element-528" data-mp="23j5sqja51n6w" />
          <Route path="/bills/financing/facility/:id" element={<ProtectedRoute data-id="element-532" data-mp="3ewj6qi1etps3">
                <AppLayout data-id="element-533" data-mp="3jp2mnlnkkx02">
                  <PageTransitionWrapper data-id="element-534" data-mp="3jqdxu6jch717">
                    <FacilityDetail data-id="element-535" data-mp="3jr5kgruyrufc" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-531" data-mp="2v8qzhdfa67zw" />
          <Route path="/bill-detail/:id" element={<ProtectedRoute data-id="element-537" data-mp="1j6aeyuqftgz">
                <AppLayout data-id="element-538" data-mp="1k93ndfj2802">
                  <PageTransitionWrapper data-id="element-539" data-mp="1hvaja4er5v5">
                    <BillDetail data-id="element-540" data-mp="6j2ipaagq72c" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-536" data-mp="3a0axfl9w8qsu" />
          <Route path="/bill-form" element={<ProtectedRoute data-id="element-542" data-mp="3sofwwgjipplu">
                <AppLayout data-id="element-543" data-mp="3srtw5fwvxip2">
                  <PageTransitionWrapper data-id="element-544" data-mp="2cb962d73t1jw">
                    <BillForm data-id="element-545" data-mp="2cafxr7shy3zn" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-541" data-mp="3ntaazhq48axr" />
          <Route path="/bill-approval/:id" element={<ProtectedRoute data-id="element-547" data-mp="ue4hfsdeby41">
                <AppLayout data-id="element-548" data-mp="ud2tinba7h4w">
                  <PageTransitionWrapper data-id="element-549" data-mp="udw45zdd2qsb">
                    <BillApprovalWizard data-id="element-550" data-mp="18dnbjh9dh8xc" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-546" data-mp="zcpgvpktqdrf" />
          <Route path="/bill-financing/:id" element={<ProtectedRoute data-id="element-552" data-mp="gn95263ouyvp">
                <AppLayout data-id="element-553" data-mp="gkd6cfk0o667">
                  <PageTransitionWrapper data-id="element-554" data-mp="bo6ajflc4b1z">
                    <BillFinancingWizard data-id="element-555" data-mp="bp7nxqptp9mv" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-551" data-mp="14d3jut7kixil" />
          <Route path="/disputes" element={<ProtectedRoute data-id="element-557" data-mp="3t44lz53oasgb">
                <AppLayout data-id="element-558" data-mp="3t3cqdua2suq1">
                  <PageTransitionWrapper data-id="element-559" data-mp="3t2l6vhtr6ng4">
                    <Disputes data-id="element-560" data-mp="3p2su0sxbtv4y" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-556" data-mp="ko2s6x9b49nj" />
          <Route path="/dispute-detail/:id" element={<ProtectedRoute data-id="element-562" data-mp="2hd9afqi09aob">
                <AppLayout data-id="element-563" data-mp="2hc7o2pvjh1ra">
                  <PageTransitionWrapper data-id="element-564" data-mp="2dch2d0kvdbtm">
                    <DisputeDetail data-id="element-565" data-mp="2ddifrbpcyaei" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-561" data-mp="2m6llsyxobkz4" />
          <Route path="/disputes/:id" element={<ProtectedRoute data-id="element-567" data-mp="351sh61lepfea">
                <AppLayout data-id="element-568" data-mp="311rvpwzw43xq">
                  <PageTransitionWrapper data-id="element-569" data-mp="313mi7fqt843x">
                    <DisputeDetail data-id="element-570" data-mp="310gtw2q09699" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-566" data-mp="353mvuy634b9x" />
          <Route path="/disputes/:id/challenge" element={<ProtectedRoute data-id="element-572" data-mp="1kp30qp687s36">
                <ChallengeDispute data-id="element-573" data-mp="1kpcu8mqhchdm" />
              </ProtectedRoute>} data-id="element-571" data-mp="2r3c3trt1gqn6" />
          <Route path="/refunds" element={<ProtectedRoute data-id="element-575" data-mp="15lt8k6p74q0s">
                <AppLayout data-id="element-576" data-mp="15ml118nhy7mu">
                  <PageTransitionWrapper data-id="element-577" data-mp="15j73qetowom9">
                    <Refunds data-id="element-578" data-mp="1al66bmhlvhi5" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-574" data-mp="1fndpb8f3dcek" />
          <Route path="/refund-detail/:id" element={<ProtectedRoute data-id="element-580" data-mp="1kb8wlexcehcs">
                <AppLayout data-id="element-581" data-mp="1objaddrdmc27">
                  <PageTransitionWrapper data-id="element-582" data-mp="1ocb047yalfkk">
                    <RefundDetail data-id="element-583" data-mp="1odm1y286gd91" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-579" data-mp="1fhczeeowkyio" />
          <Route path="/custom-metrics" element={<ProtectedRoute data-id="element-585" data-mp="238ijx75sgezb">
                <AppLayout data-id="element-586" data-mp="237h5cspkdacu">
                  <PageTransitionWrapper data-id="element-587" data-mp="236pji9lrqqzr">
                    <CustomMetrics data-id="element-588" data-mp="3w7kfw5qvz68" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-584" data-mp="1yaqq27ll2ish" />
          <Route path="/nadi" element={<ProtectedRoute data-id="element-590" data-mp="uupgdzrsqg3">
                <AppLayout data-id="element-591" data-mp="w5vkkgouaad">
                  <PageTransitionWrapper data-id="element-592" data-mp="3s0mefa1t9x1b">
                    <Nadi data-id="element-593" data-mp="3s1xh16jisyqu" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-589" data-mp="8wvd0m9mgo2k" />
          <Route path="/reports" element={<ProtectedRoute data-id="element-595" data-mp="5v9083sraeht">
                <AppLayout data-id="element-596" data-mp="jsnv3o1s3eh6">
                  <PageTransitionWrapper data-id="element-597" data-mp="jrvyc9wg3apb">
                    <Reports data-id="element-598" data-mp="jquo27n96s8n" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-594" data-mp="5tfr8huo7i6n" />
          <Route path="/card-monitoring" element={<ProtectedRoute data-id="element-600" data-mp="oj46l2m1gq0w">
                <AppLayout data-id="element-601" data-mp="okytgmgves7m">
                  <PageTransitionWrapper data-id="element-602" data-mp="olqpfyedqryf">
                    <CardMonitoringPrograms data-id="element-603" data-mp="12ix69nfjmgl0" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-599" data-mp="thp7l4947dqe" />
          <Route path="/card-monitoring/vamp" element={<ProtectedRoute data-id="element-605" data-mp="1h6x9jp38q1ii">
                <AppLayout data-id="element-606" data-mp="1m4ypw29a7mqa">
                  <PageTransitionWrapper data-id="element-607" data-mp="1m45q5l8qpxhn">
                    <VampDetail data-id="element-608" data-mp="1m34cra494ywr" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-604" data-mp="yg0izjf25waq" />
          <Route path="/payment-analytics" element={<ProtectedRoute data-id="element-610" data-mp="yf4elp4m2685">
                <AppLayout data-id="element-611" data-mp="yeuo80fnlx1x">
                  <PageTransitionWrapper data-id="element-612" data-mp="ydjj9xah2j98">
                    <PaymentAnalytics data-id="element-613" data-mp="136mesayt82s2" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-609" data-mp="1d3hcwsr6815t" />
          <Route path="/activity-logs" element={<ProtectedRoute data-id="element-615" data-mp="th2xvaunqizu">
                <AppLayout data-id="element-616" data-mp="fgs2buiekg8c">
                  <PageTransitionWrapper data-id="element-617" data-mp="fgias2hpm10j">
                    <ActivityLogs data-id="element-618" data-mp="fhjxj4838by3" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-614" data-mp="pd4ugnyaum0z" />
          <Route path="/requests" element={<ProtectedRoute data-id="element-620" data-mp="1irnzbunk5a80">
                <AppLayout data-id="element-621" data-mp="1irfqqeygwr2m">
                  <PageTransitionWrapper data-id="element-622" data-mp="1is7i1dl182n3">
                    <Requests data-id="element-623" data-mp="1e0pj0v2kkc4r" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-619" data-mp="1mtj2z5synzoy" />
          <Route path="/requests/:id" element={<ProtectedRoute data-id="element-625" data-mp="143ukwda7kdr0">
                <AppLayout data-id="element-626" data-mp="14430icxptx5w">
                  <PageTransitionWrapper data-id="element-627" data-mp="z3oqipck0d58">
                    <RequestDetail data-id="element-628" data-mp="z2x5g8gl1xt7" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-624" data-mp="191cvs97sd31n" />
        </Routes>
      </Suspense>
    </BrowserRouter>;
}